import { db } from '../firebase';
import {
  collection, getDocs,
  doc, query, orderBy, getDoc, serverTimestamp
} from 'firebase/firestore';
import { logService } from './logService';
import { addDoc, updateDoc, deleteDoc } from './guardedDb';

const BASE_KEY = 'E2B_RECALL_BASE'; 
const EXEC_KEY = 'E2B_RECALL_EXECUTION'; 

export const recallService = {
  getRecallBase: async () => {
    try {
      const q = query(collection(db, BASE_KEY), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (e) { return []; }
  },

  addRecallBase: async (data) => {
    try {
      const { id, ...pureData } = data;
      const ref = await addDoc(collection(db, BASE_KEY), {
        ...pureData,
        createdAt: serverTimestamp(),
        regDate: new Date().toISOString().split('T')[0]
      });
      logService.record({
        module: '리콜관리', action: 'CREATE',
        target: `리콜 [${pureData.title || pureData.modelName || '미지정'}]`,
        details: `리콜 기초 항목 생성 (차종: ${pureData.modelName || '-'}, 사유: ${String(pureData.reason || '-').substring(0, 40)})`
          + (pureData.source && pureData.source.system === 'VIMS' ? ' — VIMS에서 가져옴' : '')
      });
      return ref.id;
    } catch (e) { throw e; }
  },

  updateRecallBase: async (id, data) => {
    try {
      const { id: _, ...pureData } = data;
      await updateDoc(doc(db, BASE_KEY, id), { ...pureData, updatedAt: serverTimestamp() });
      logService.record({
        module: '리콜관리', action: 'UPDATE',
        target: `리콜 [${pureData.title || id}]`,
        details: `리콜 기초 항목 수정 (차종: ${pureData.modelName || '-'})`
      });
    } catch (e) { throw e; }
  },

  deleteRecallBase: async (id) => {
    try {
      // 무엇이 지워졌는지 남기기 위해 삭제 전에 제목을 확보한다(감사 목적).
      let label = id;
      try {
        const snap = await getDoc(doc(db, BASE_KEY, id));
        if (snap.exists()) label = snap.data().title || snap.data().modelName || id;
      } catch (_) { /* 확보 실패해도 삭제는 진행 */ }

      await deleteDoc(doc(db, BASE_KEY, id));
      logService.record({
        module: '리콜관리', action: 'DELETE',
        target: `리콜 [${label}]`,
        details: '리콜 기초 항목 삭제'
      });
    } catch (e) { throw e; }
  },

  getRecallExecutions: async () => {
    try {
      const q = query(collection(db, EXEC_KEY), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (e) { return []; }
  },

  // [수정] UI에서 전달받은 데이터를 파이어베이스 규격에 맞게 정규화하여 저장합니다.
  startRecallExecution: async (formData, targets) => {
    try {
      await addDoc(collection(db, EXEC_KEY), {
        baseId: formData.baseId || "", // 정확한 정책 ID 매칭
        title: formData.title || "미정", 
        reason: formData.reason || "상세 내용 없음", // 누락된 결함 원인 추가
        modelName: formData.modelName || "공용",
        targets: targets || [],
        status: '진행중',
        regDate: new Date().toLocaleDateString(),
        createdAt: serverTimestamp()
      });
      logService.record({
        module: '리콜관리', action: 'CREATE',
        target: `리콜캠페인 [${formData.title || '미정'}]`,
        details: `리콜 이행 시작 (차종: ${formData.modelName || '공용'}, 대상 ${(targets || []).length}대)`
      });
    } catch (e) {
      console.error("Firestore 저장 에러:", e);
      throw e;
    }
  },

  // 캠페인 수정 — '삭제 후 재생성'은 중간에 실패하면 조치 이력까지 통째로 사라지므로 제자리에서 고친다.
  // status·createdAt·regDate 는 건드리지 않아 기존 진행 상태가 보존된다.
  updateRecallExecution: async (id, formData, targets) => {
    try {
      if (!id) throw new Error("수정할 캠페인 ID가 없습니다.");
      await updateDoc(doc(db, EXEC_KEY, id), {
        baseId: formData.baseId || "",
        title: formData.title || "미정",
        reason: formData.reason || "상세 내용 없음",
        modelName: formData.modelName || "공용",
        targets: targets || [],
        updatedAt: serverTimestamp()
      });
      logService.record({
        module: '리콜관리', action: 'UPDATE',
        target: `리콜캠페인 [${formData.title || id}]`,
        details: `리콜 캠페인 수정 (차종: ${formData.modelName || '공용'}, 대상 ${(targets || []).length}대)`
      });
    } catch (e) { throw e; }
  },

  saveVehicleAction: async (execId, carNumber, actionData) => {
    try {
      const docRef = doc(db, EXEC_KEY, execId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const updatedTargets = docSnap.data().targets.map(t => {
          if (t.carNumber === carNumber) {
            return { ...t, status: '완료', doneDate: new Date().toLocaleDateString(), actionData: actionData };
          }
          return t;
        });
        await updateDoc(docRef, { targets: updatedTargets });
        logService.record({
          module: '리콜관리', action: 'UPDATE',
          target: `차량 [${carNumber}]`,
          details: `리콜 조치 완료 처리 (캠페인: ${docSnap.data().title || '-'})`
        });
      }
    } catch (e) { throw e; }
  },

  remove: async (id) => {
    try {
      // 무엇이 지워졌는지 남기기 위해 삭제 전에 캠페인명을 확보한다(감사 목적).
      let label = id;
      try {
        const snap = await getDoc(doc(db, EXEC_KEY, id));
        if (snap.exists()) label = snap.data().title || id;
      } catch (_) { /* 확보 실패해도 삭제는 진행 */ }

      await deleteDoc(doc(db, EXEC_KEY, id));
      logService.record({
        module: '리콜관리', action: 'DELETE',
        target: `리콜캠페인 [${label}]`,
        details: '리콜 이행 건 삭제'
      });
    } catch (e) { throw e; }
  }
};